import type { ChangeEvent } from 'react';
import { translateKey } from '../../translate';
import { Component } from './Component';

export interface IFile {
    data: string | null;
    fileName: string | null;
}

interface IFileInputProps {
    extension?: string;
    fileName?: string | null;
    files?: IFile[] | null;
    label?: string;
    multiple?: boolean;
    onChange?: (result: IFile) => Promise<void>;
    onChangeMultiple?: (result: IFile[] | null) => Promise<void>;
    onError: (error: string) => Promise<void>;
}

export class FileInput extends Component<IFileInputProps, {}> {
    private filesRead: IFile[] = [];

    public render() {
        return (
            <>
                {!this.props.fileName && (!this.props.files || this.props.files.length === 0) && (
                    <label className="btn btn-secondary">
                        {this.renderFileInput()}
                        {this.props.label || translateKey('upload.chooseFile')}
                    </label>
                )}

                {this.props.fileName && (
                    <label
                        className="btn-link pt10"
                        key={this.props.fileName || undefined}
                    >
                        {this.renderFileInput()}
                        {this.props.fileName}
                    </label>
                )}

                {this.props.files && this.props.files.length > 0 && (
                    <div>
                        {this.props.files.map((file) => {
                            return (
                                <label
                                    className="btn-link pt10"
                                    key={file.fileName || undefined}
                                >
                                    {this.renderFileInput()}
                                    {file.fileName}
                                </label>
                            );
                        })}
                    </div>
                )}
            </>
        );
    }

    private renderFileInput() {
        return (
            <input
                accept={this.props.extension ?? '.csv'}
                className="hidden"
                multiple={this.props.multiple}
                onChange={async (event) => await this.handleFile(event)}
                type="file"
            />
        );
    }

    private async handleFile(event: ChangeEvent<HTMLInputElement>) {
        if (event.target && event.target.files && event.target.files.length > 0) {
            const reader = new FileReader();
            const eventFiles = [...event.target.files];

            reader.addEventListener(
                'load',
                async () => {
                    const result = reader.result as string;
                    const file = {
                        fileName: eventFiles[this.filesRead.length].name,
                        data: result.substring(result.indexOf(',') + 1), // Takes the base64 value of the reader
                    };
                    this.filesRead.push(file);
                    this.readFiles(reader, eventFiles);
                },
                false
            );
            reader.addEventListener(
                'error',
                async () => {
                    if (this.props.multiple && this.props.onChangeMultiple) {
                        await this.props.onChangeMultiple(null);
                    }

                    if (!this.props.multiple && this.props.onChange) {
                        await this.props.onChange({ fileName: null, data: null });
                    }
                    await this.props.onError(translateKey('imports.addFailed', eventFiles[this.filesRead.length].name));
                },
                false
            );

            this.readFiles(reader, eventFiles);

            event.target.value = '';
        }
    }

    private async readFiles(reader: FileReader, files: any) {
        if (this.filesRead.length === files.length) {
            if (this.props.multiple && this.props.onChangeMultiple) {
                await this.props.onChangeMultiple(this.filesRead);
            }

            if (!this.props.multiple && this.props.onChange) {
                await this.props.onChange(this.filesRead[0]);
            }

            this.filesRead = [];

            return;
        }

        reader.readAsDataURL(files[this.filesRead.length]);
    }
}
